import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Camera, CheckCircle, Globe, MapPin, SealCheck, ShieldWarning,
  Sparkle, UploadSimple, X,
} from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_LADDER = [
  { key: "UNIDENTIFIED", label: "Unidentified" },
  { key: "AI_SUGGESTED", label: "AI suggested" },
  { key: "PROBABLE", label: "Probable" },
  { key: "DATABASE_MATCHED", label: "Database matched" },
  { key: "COMMUNITY_CONFIRMED", label: "Community confirmed" },
  { key: "EXPERT_VERIFIED", label: "Expert verified" },
];

const HABITATS = ["", "Garden / backyard", "City park", "Farm / field", "Forest", "Roadside", "Wetland / waterside", "Desert", "Mountain / alpine", "Indoor / houseplant", "Coastal"];

// IUCN short codes → readable labels (provider data arrives as codes)
const IUCN_LABELS = {
  EX: "Extinct", EW: "Extinct in the wild", CR: "Critically Endangered",
  EN: "Endangered", VU: "Vulnerable", NT: "Near Threatened", LC: "Least Concern",
  DD: "Data Deficient", NE: "Not Evaluated",
};
const iucnLabel = (code) => IUCN_LABELS[(code || "").toUpperCase()] || code;

const WORKING_STEPS = [
  "Reading your photograph…",
  "Generating candidate identifications…",
  "Validating against taxonomic databases…",
  "Checking geographic plausibility…",
  "Scoring confidence…",
];

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

/* ---------------- status ladder ---------------- */
function StatusLadder({ status }) {
  const active = STATUS_LADDER.findIndex((s) => s.key === status);
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="identify-status-ladder">
      {STATUS_LADDER.map((s, i) => (
        <span key={s.key} className="inline-flex items-center gap-1">
          <span className={`rounded-full px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase border ${
            i === active ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
              : i < active ? "bg-[#1B4332]/10 text-[#1B4332] border-[#1B4332]/25"
              : "text-[#3A3830]/50 border-black/10"}`}>
            {s.label}
          </span>
          {i < STATUS_LADDER.length - 1 && <span className="text-[#3A3830]/30 text-[9px]">→</span>}
        </span>
      ))}
    </div>
  );
}

/* ---------------- confidence bar ---------------- */
function ConfidenceBar({ value, testid }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="h-1.5 w-28 rounded-full bg-black/10 overflow-hidden">
        <div className="h-full rounded-full bg-[#1B4332] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono-tech text-[10px] tracking-widest text-[#3A3830]/70">{pct}%</span>
    </div>
  );
}

/* ---------------- save modal ---------------- */
function SaveModal({ session, candidate, rank, onClose }) {
  const [notes, setNotes] = useState("");
  const [observedOn, setObservedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [shareLocation, setShareLocation] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/identify/v2/${session.ident_id}/save`, {
        candidate_rank: rank,
        notes: notes || undefined,
        observed_on: observedOn || undefined,
        share_location: shareLocation,
      });
      setSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="identify-save-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-[color:var(--np-bg,#F5EBDC)] border border-black/10 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#1B4332]">Save to NaturePulse</div>
          <button onClick={onClose} data-testid="identify-save-close" className="rounded-full border border-black/15 p-1.5 hover:bg-black/5" aria-label="Close">
            <X size={14} weight="bold" />
          </button>
        </div>

        {saved ? (
          <div className="py-4 text-center" data-testid="identify-saved">
            <CheckCircle size={40} weight="fill" className="mx-auto text-[#1B4332]" />
            <p className="mt-3 font-serif-display text-xl">{saved.message}</p>
            {saved.observation_id && saved.moderation_status === "approved" && (
              <Link to={`/observations/${saved.observation_id}`} data-testid="identify-saved-view"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.26em] uppercase">
                View observation <ArrowRight size={12} weight="bold" />
              </Link>
          )}
          </div>
        ) : (
          <>
            <p className="text-sm text-[#3A3830]">
              This becomes a NaturePulse observation linked to the species record
              {candidate.catalogue ? <> — it will appear on the <b>{candidate.catalogue.title}</b> catalogue page</> : ""}.
            </p>
            <label className="mt-4 block font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              data-testid="identify-save-notes"
              placeholder="Where you found it, condition, flowering stage…"
              className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <label className="mt-3 block font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70">Observed on</label>
            <input type="date" value={observedOn} onChange={(e) => setObservedOn(e.target.value)}
              data-testid="identify-save-date"
              className="mt-1 bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            {session.context?.lat != null && (
              <label className="mt-3 flex items-start gap-2 text-sm text-[#3A3830]" data-testid="identify-save-location">
                <input type="checkbox" checked={shareLocation} onChange={(e) => setShareLocation(e.target.checked)} className="mt-1" />
                <span>Share an <b>approximate</b> location with the community (never your exact spot).</span>
              </label>
            )}
            <button onClick={submit} disabled={busy} data-testid="identify-save-submit"
              className="mt-5 w-full rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-40">
              {busy ? "Saving…" : "Save observation"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- community feedback (confirm / correct) ---------------- */
function CandidateFeedback({ session, candidate, rank, onSession }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [correction, setCorrection] = useState("");
  const count = candidate.community_confirmations || 0;

  const send = async (payload) => {
    setBusy(true);
    try {
      const { data } = await http.post(`/identify/v2/${session.ident_id}/feedback`, payload);
      onSession({ ...session, final_status: data.final_status, candidates: session.candidates.map((c, i) => i === rank ? { ...c, community_confirmations: data.community_confirmations ?? count } : c) });
      setDone(true);
      setCorrecting(false);
      toast.success(payload.action === "confirm" ? "Thanks — confirmation recorded" : "Correction sent to our stewards");
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401) toast.error("Sign in to confirm or correct identifications");
      else if (s === 409) { toast.info("You've already given feedback here"); setDone(true); }
      else toast.error(e?.response?.data?.detail || "Couldn't record that — try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.18em] uppercase" data-testid={`identify-feedback-done-${rank + 1}`}>
        <CheckCircle size={11} weight="fill" /> Feedback recorded
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid={`identify-feedback-${rank + 1}`}>
      <button disabled={busy} onClick={() => (user ? send({ candidate_rank: rank + 1, action: "confirm" }) : toast.error("Sign in to confirm identifications"))}
        data-testid={`identify-confirm-btn-${rank + 1}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#2D6A4F]/40 text-[#2D6A4F] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.18em] uppercase hover:bg-[#2D6A4F] hover:text-[#F5EBDC] transition-colors disabled:opacity-40">
        <CheckCircle size={11} weight="bold" /> Saw this too{count > 0 ? ` (${count})` : ""}
      </button>
      {correcting ? (
        <span className="inline-flex items-center gap-1.5">
          <input value={correction} onChange={(e) => setCorrection(e.target.value)} maxLength={300}
            placeholder="What do you think it is?" data-testid={`identify-correction-input-${rank + 1}`}
            className="rounded-full border border-black/15 bg-white px-3 py-1.5 text-xs w-52" />
          <button disabled={busy || !correction.trim()} onClick={() => send({ candidate_rank: rank + 1, action: "correction", correction_text: correction.trim() })}
            data-testid={`identify-correction-send-${rank + 1}`}
            className="rounded-full bg-[#B85F2A] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.18em] uppercase disabled:opacity-40">
            Send
          </button>
          <button onClick={() => setCorrecting(false)} className="text-[#3A3830]/50 hover:text-[#3A3830]" aria-label="Cancel correction">
            <X size={12} weight="bold" />
          </button>
        </span>
      ) : (
        <button onClick={() => (user ? setCorrecting(true) : toast.error("Sign in to suggest a correction"))}
          data-testid={`identify-correct-btn-${rank + 1}`}
          className="inline-flex items-center gap-1 rounded-full border border-black/15 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#3A3830]/70 hover:bg-black/5">
          Suggest different ID
        </button>
      )}
    </span>
  );
}

/* ---------------- main page ---------------- */
export default function IdentifyPage() {
  const { identId } = useParams();
  const [photos, setPhotos] = useState([]); // {file, preview}
  const [habitat, setHabitat] = useState("");
  const [notes, setNotes] = useState("");
  const [coords, setCoords] = useState(null);
  const [locBusy, setLocBusy] = useState(false);
  const [working, setWorking] = useState(false);
  const [workStep, setWorkStep] = useState(0);
  const [session, setSession] = useState(null);
  const [saveTarget, setSaveTarget] = useState(null); // {candidate, rank}
  const fileRef = useRef(null);

  // Load an existing session via /identify/:identId
  useEffect(() => {
    if (!identId) return;
    http.get(`/identify/v2/${identId}`)
      .then(({ data }) => setSession(data))
      .catch(() => toast.error("Identification not found"));
  }, [identId]);

  // Accept a photo handed over from the SpeciesPulse hero upload card
  useEffect(() => {
    const pre = sessionStorage.getItem("np_identify_prefill");
    if (!pre) return;
    sessionStorage.removeItem("np_identify_prefill");
    fetch(pre)
      .then((r) => r.blob())
      .then((b) => setPhotos([{ file: new File([b], "photo.jpg", { type: b.type || "image/jpeg" }), preview: pre }]))
      .catch(() => {});
  }, []);

  // Rotate the working messages while the pipeline runs
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => setWorkStep((s) => Math.min(s + 1, WORKING_STEPS.length - 1)), 3500);
    return () => clearInterval(t);
  }, [working]);

  const addPhotos = (files) => {
    const next = [...photos];
    for (const f of files) {
      if (next.length >= 5) break;
      next.push({ file: f, preview: URL.createObjectURL(f) });
    }
    setPhotos(next);
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported here");
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lon: p.coords.longitude }); setLocBusy(false); toast.success("Approximate location attached"); },
      () => { setLocBusy(false); toast.error("Couldn't get location — identification still works without it"); },
      { timeout: 10000 },
    );
  };

  const submit = async () => {
    if (!photos.length) return;
    setWorking(true); setWorkStep(0);
    try {
      const images = await Promise.all(photos.map((p) => new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(p.file);
      })));
      const { data } = await http.post("/identify/v2", {
        images,
        lat: coords?.lat, lon: coords?.lon,
        habitat: habitat || undefined, notes: notes || undefined,
      }, { timeout: 150000 });
      setSession(data);
      window.history.replaceState(null, "", `/identify/${data.ident_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Identification failed — try a clearer photo");
    } finally {
      setWorking(false);
    }
  };

  const reset = () => { setSession(null); setPhotos([]); setNotes(""); setHabitat(""); setSaveTarget(null); window.history.replaceState(null, "", "/identify"); };

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="identify-page">
      <SeoMeta
        title="NaturePulse Identify — photograph nature, discover what you're seeing"
        description="Upload a photo of a plant, bird, insect or fungus. NaturePulse Identify generates candidates with visual AI, validates them against taxonomic databases, checks geography, and shows you exactly how confident each answer is."
        canonical={`${origin}/identify`}
      />
      <div className="max-w-[980px] mx-auto px-5 md:px-8">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Identify" }]} />

        {!session ? (
          <>
            <h1 className="mt-4 font-serif-display text-4xl md:text-6xl leading-[1.0] tracking-tight" data-testid="identify-title">
              Photograph nature.<br />Discover what you're seeing.
            </h1>
            <p className="mt-4 max-w-2xl text-base md:text-lg text-[#3A3830] leading-relaxed">
              Upload up to five photos of the same plant, bird, insect or fungus. NaturePulse Identify
              generates candidates with visual AI, validates them against open taxonomic databases,
              checks whether the species is expected where you are — and shows you exactly how far
              each answer has been verified.
            </p>

            <div className="mt-8 rounded-2xl bg-white border border-black/10 p-5 md:p-7" data-testid="identify-uploader">
              <div
                className="rounded-xl border-2 border-dashed border-black/15 hover:border-[#1B4332]/50 transition-colors p-8 text-center cursor-pointer"
                data-testid="identify-dropzone"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); addPhotos(e.dataTransfer.files); }}
              >
                <UploadSimple size={28} weight="bold" className="mx-auto text-[#1B4332]" />
                <div className="mt-2 font-serif-display text-xl">Drop photos here, or tap to choose</div>
                <div className="mt-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830]/60">
                  1–5 photos of the same organism · JPG/PNG · ≤ 8 MB each
                </div>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden
                  data-testid="identify-file-input"
                  onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
              </div>

              {photos.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3" data-testid="identify-previews">
                  {photos.map((p, i) => (
                    <div key={p.preview} className="relative">
                      <img src={p.preview} alt={`upload ${i + 1}`} className="w-24 h-24 object-cover rounded-lg border border-black/10" />
                      <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                        className="absolute -top-2 -right-2 rounded-full bg-[#7C2D12] text-white p-1" aria-label="Remove photo">
                        <X size={10} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <button onClick={locate} disabled={locBusy} data-testid="identify-locate"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1B4332]/30 px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#1B4332] hover:bg-[#1B4332] hover:text-[#F5EBDC] transition-colors">
                  <MapPin size={13} weight="bold" />
                  {coords ? `${coords.lat.toFixed(2)}, ${coords.lon.toFixed(2)}` : locBusy ? "Locating…" : "Attach my location"}
                </button>
                <select value={habitat} onChange={(e) => setHabitat(e.target.value)} data-testid="identify-habitat"
                  className="rounded-full border border-black/15 bg-white px-4 py-2.5 text-sm">
                  {HABITATS.map((h) => <option key={h} value={h}>{h || "Habitat (optional)"}</option>)}
                </select>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
                  data-testid="identify-notes"
                  className="rounded-full border border-black/15 bg-white px-4 py-2.5 text-sm" />
              </div>

              <button onClick={submit} disabled={!photos.length || working} data-testid="identify-submit"
                className="mt-6 w-full rounded-full bg-[#1B4332] text-[#F5EBDC] px-6 py-3.5 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-40">
                {working ? "Identifying…" : "Identify this"}
              </button>

              {working && (
                <div className="mt-4 text-center" data-testid="identify-working">
                  <Sparkle size={18} weight="fill" className="inline text-[#B85F2A] animate-pulse" />
                  <span className="ml-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70">{WORKING_STEPS[workStep]}</span>
                </div>
              )}
            </div>

            <div className="mt-6 rounded-xl border border-black/10 bg-white/60 p-4 text-[13px] text-[#3A3830] leading-relaxed" data-testid="identify-honesty-note">
              <b>Honest by design.</b> An AI answer is a candidate, never a verdict. Every result shows a
              verification ladder — AI suggested → probable → database matched → community confirmed →
              expert verified — and we never call a wild plant or mushroom safe to eat from a photo.
            </div>
          </>
        ) : (
          /* ------------------------------ RESULT ------------------------------ */
          <div data-testid="identify-result">
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#B85F2A]" data-testid="identify-broad-class">
                  {(session.broad_class || "unknown").replace("-", " ")}
                </div>
                <h1 className="font-serif-display text-3xl md:text-5xl leading-tight" data-testid="identify-result-title">
                  {session.candidates?.[0]?.common_name || session.candidates?.[0]?.scientific_name || "No confident match"}
                </h1>
                {session.candidates?.[0]?.scientific_name && (
                  <div className="font-serif-display italic text-lg text-[#3A3830]/80" data-testid="identify-result-sci">
                    {session.candidates[0].scientific_name}
                  </div>
                )}
              </div>
              <button onClick={reset} data-testid="identify-again"
                className="rounded-full border border-black/15 px-4 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5">
                Identify another
              </button>
            </div>

            <div className="mt-4"><StatusLadder status={session.final_status} /></div>

            {session.media?.length > 0 && (
              <div className="mt-5 flex gap-3 overflow-x-auto" data-testid="identify-result-photos">
                {session.media.map((m) => (
                  <img key={m.url} src={abs(m.url)} alt="your upload" className="h-28 rounded-lg border border-black/10 object-cover" />
                ))}
              </div>
            )}

            {/* Safety */}
            {session.safety_notes?.length > 0 && (
              <div className="mt-5 rounded-xl border border-[#7C2D12]/25 bg-[#7C2D12]/6 p-4 text-sm text-[#7C2D12] space-y-1.5" data-testid="identify-safety">
                {session.safety_notes.map((n) => (
                  <div key={n.slice(0, 24)} className="flex gap-2"><ShieldWarning size={16} weight="fill" className="shrink-0 mt-0.5" />{n}</div>
                ))}
              </div>
            )}

            {/* Candidates */}
            <div className="mt-8 space-y-4">
              {(session.candidates || []).map((c, i) => (
                <div key={i} className={`rounded-2xl bg-white border p-5 ${i === 0 ? "border-[#1B4332]/40 shadow-sm" : "border-black/10"}`}
                  data-testid={`identify-candidate-${i + 1}`}>
                  <div className="flex flex-wrap items-start gap-4">
                    {(c.inat?.photo) && (
                      <img src={c.inat.photo} alt={c.common_name || c.scientific_name} className="w-24 h-24 rounded-lg object-cover border border-black/10" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-serif-display text-xl md:text-2xl" data-testid={`identify-candidate-${i + 1}-name`}>
                          {c.common_name || c.scientific_name || "Unnamed candidate"}
                        </span>
                        {c.scientific_name && <span className="font-serif-display italic text-[#3A3830]/75">{c.scientific_name}</span>}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <ConfidenceBar value={c.combined_confidence} testid={`identify-candidate-${i + 1}-confidence`} />
                        <span className="flex flex-wrap gap-1.5">
                          {c.ai_confidence != null && (
                            <span className="rounded-full bg-black/5 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.14em] uppercase">AI {Math.round(c.ai_confidence * 100)}%</span>
                          )}
                          {c.provider_confidence != null && (
                            <span className="rounded-full bg-black/5 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.14em] uppercase">Pl@ntNet {Math.round(c.provider_confidence * 100)}%</span>
                          )}
                          {c.taxonomy_match && (
                            <span className="rounded-full bg-[#0C4A6E]/10 text-[#0C4A6E] px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.14em] uppercase inline-flex items-center gap-1">
                              <SealCheck size={9} weight="fill" /> Taxonomy matched
                            </span>
                          )}
                          {c.geographic_match === true && (
                            <span className="rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.14em] uppercase inline-flex items-center gap-1">
                              <Globe size={9} weight="fill" /> Seen nearby
                            </span>
                          )}
                          {c.geographic_match === false && (
                            <span className="rounded-full bg-[#B85F2A]/10 text-[#B85F2A] px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.14em] uppercase">
                              Not recorded nearby
                            </span>
                          )}
                        </span>
                      </div>

                      {c.identifying_characteristics?.length > 0 && (
                        <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#22301F]">
                          {c.identifying_characteristics.map((ch) => <li key={ch}>· {ch}</li>)}
                        </ul>
                      )}
                      {(c.habitat || c.native_range || c.seasonality) && (
                        <div className="mt-3 grid sm:grid-cols-3 gap-3 text-[13px] text-[#3A3830]">
                          {c.habitat && <div><span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#3A3830]/60 block">Habitat</span>{c.habitat}</div>}
                          {c.native_range && <div><span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#3A3830]/60 block">Native range</span>{c.native_range}</div>}
                          {c.seasonality && <div><span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#3A3830]/60 block">Seasonality</span>{c.seasonality}</div>}
                        </div>
                      )}
                      {c.similar_species?.length > 0 && (
                        <div className="mt-2 text-[13px] text-[#3A3830]/80">
                          <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#3A3830]/60">Look-alikes: </span>
                          {c.similar_species.join(", ")}
                        </div>
                      )}
                      {c.conservation_status && (
                        <div className="mt-2 text-[13px] text-[#3A3830]">
                          <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#3A3830]/60">Conservation: </span>
                          {iucnLabel(c.conservation_status.status)} <span className="text-[#3A3830]/60">({c.conservation_status.source})</span>
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {c.catalogue && (
                          <Link to={`/explore/${c.catalogue.hub}/${c.catalogue.slug}`} data-testid={`identify-candidate-${i + 1}-catalogue`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#0C4A6E]/10 text-[#0C4A6E] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase hover:bg-[#0C4A6E]/20">
                            In our catalogue: {c.catalogue.title} <ArrowRight size={11} weight="bold" />
                          </Link>
                        )}
                        {c.inat?.wikipedia_url && (
                          <a href={c.inat.wikipedia_url} target="_blank" rel="noreferrer" data-testid={`identify-candidate-${i + 1}-wikipedia`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase hover:bg-black/5">
                            Wikipedia
                          </a>
                        )}
                        <button onClick={() => setSaveTarget({ candidate: c, rank: i + 1 })} data-testid={`identify-save-btn-${i + 1}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase transition-colors ${
                            i === 0 ? "bg-[#B85F2A] text-[#F5EBDC] hover:opacity-90" : "border border-[#B85F2A]/40 text-[#B85F2A] hover:bg-[#B85F2A] hover:text-[#F5EBDC]"}`}>
                          <Camera size={13} weight="bold" /> Save to NaturePulse
                        </button>
                        <CandidateFeedback session={session} candidate={c} rank={i} onSession={setSession} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-[12px] text-[#3A3830]/70" data-testid="identify-attribution">
              Evidence from: {(session.providers_used || []).join(" · ")}. Identifications are candidates for
              verification — the community and expert stewards complete the ladder.
            </div>
          </div>
        )}

        {saveTarget && session && (
          <SaveModal session={session} candidate={saveTarget.candidate} rank={saveTarget.rank} onClose={() => setSaveTarget(null)} />
        )}
      </div>
    </main>
  );
}
